/* eslint-disable camelcase */
import React from "react";
import dynamic from "next/dynamic";
import Head from "next/head";
import getConfig from "next/config";
import cn from "classnames";
import { useDebounce } from "react-use";
import deepEqual from "deep-equal";
import { useRouter } from "next/router";
import { v4 as uuidv4 } from "uuid";

import { JobSearchHit, JobSearchLocation } from "@themuse/design-system";
import { triggerSnowplowSearchEvent, SnowplowSearchEventProps } from "@themuse/snowplow-js-client";

import { generateUrlPath } from "utils/browser";
import { fetchJobs } from "utils/api";
import { normalizeLocationName } from "utils/strings";
import { getParams } from "utils/searchResults";
import { useSearchResultsState, useSearchResultsDispatch } from "hooks/SearchResultsContext";
import {
    UserInteractionActionTypes,
    CompanyActionTypes,
    LocationActionTypes,
    KeywordActionTypes,
    FiltersActionTypes,
    UrlActionTypes,
    SearchIdActionTypes,
    UserInteractions,
    SearchDispatch,
} from "interfaces/context/searchContextInterfaces";
import { searchResultsSelector } from "selectors";
import { useSearchState, useSearchDispatch } from "hooks/SearchContext";
import { useUserState } from "hooks/UserContext";
import { useFeatureFlagsState } from "hooks/FeatureFlagsContext";
import { formatJobsResponse } from "orm/jobs";
import { buildSnowplowFilterItems } from "utils/searchFacetsTracking";
import { SearchHeader } from "./SearchHeader";
import { LoadMore } from "./LoadMore";
import { JobTile } from "./JobTile";
import { Empty } from "./Empty";
import { JobAlertWrapper } from "./JobAlertWrapper";
import styles from "./SearchResults.module.scss";

interface ResultsProps {
    onClear?: () => void;
}

interface GetJobTileLocationProps {
    inputDma: string;
    locationType: string;
    locations: JobSearchLocation[];
    sessionDma: string;
}

const DynamicJobAlertWrapper = dynamic<React.ComponentProps<typeof JobAlertWrapper>>(
    () => import("./JobAlertWrapper").then((x) => x.JobAlertWrapper),
    { ssr: false }
);

/**
 * Extract the closest matching location out of a job and format it for display in a job tile.
 */
const getJobTileLocation = (props: GetJobTileLocationProps): string[] => {
    const {
        inputDma,
        locationType,
        locations,
        sessionDma
    } = props;
    const isRemote: boolean = locationType === "virtual";
    // We always prefer the dropdown if it was used.
    const userLocation: string = inputDma || sessionDma;
    // We Only care about the actual location address.
    const locationStrings: string[] = locations.map((location) => location.address);
    // This is the fully matched location (city and state).
    const matchedLocation: string = locationStrings.filter((loc) => loc === userLocation)[0];
    // We may need to match only on the last segment of the location.
    const getState = (txt: string) => {
        if (typeof txt === "string") {
            return txt.split(",")[1]?.trim()?.toLowerCase();
        }
        return null;
    };
    // First location matched by region.
    const matchedRegion: string = locationStrings.filter((address) => getState(address) === getState(userLocation))[0];
    // First location in the list (fall back)
    const firstLocation: string = locationStrings[0];
    // The location to display
    const displayLocation: string = matchedLocation || matchedRegion || firstLocation || "";
    // If we have more than 2 locations pluralize the string.
    const locationWord: string = ` location${locationStrings.length > 2 ? "s" : ""}`;
    // If we have more than 1 location we add a plus sign and the number plus the word location(s)
    const extraLocations: string = locationStrings.length > 1 ? ` + ${locationStrings.length - 1}${locationWord}` : "";
    // If we are remote and there is a location we will separate them with a slash.
    const separator: string = displayLocation ? " / " : "";
    // Build the remote string.
    const remoteString: string = isRemote ? `${separator}Remote` : "";

    // all together now...
    return [`${displayLocation}${remoteString}${extraLocations}`];
};

/**
 * Renders a list of JobTile components based on the search results passed.
 */
const Tiles = React.memo((props: {
    searchResults: JobSearchHit[],
    locationName: string,
    userInteractions: UserInteractions,
    userLocationDma: string,
    resultsLength: number,
    searchDispatch: SearchDispatch
}) => {
    const {
        searchResults,
        locationName,
        userInteractions,
        userLocationDma,
        resultsLength,
        searchDispatch
    } = props;
    const [jobAlertsViewed, setJobAlertsViewed] = React.useState(false);
    const { FEATURE_FLAG_JOBALERT_SEARCH } = useFeatureFlagsState();

    return (
        <>
            {searchResults.map((job: JobSearchHit, index) => {
                const inputDMA = locationName ?? "";
                const viewed = userInteractions.tilesViewedJobIds.includes(job.hit.id);
                const show_logo: boolean = !(["archived", "lite"].includes(job?.hit?.company?.client_tier ?? ""));

                const handleViewedCallback = (jobId: string) => {
                    searchDispatch({
                        type: UserInteractionActionTypes.updateJobTileViews,
                        payload: {
                            tilesViewedJobIds: [jobId]
                        }
                    });
                };

                const enableJobAlert = index === 3 && FEATURE_FLAG_JOBALERT_SEARCH;

                return (
                    <React.Fragment key={`${job.hit.id}`}>
                        <JobTile
                            {...{
                                job,
                                index,
                                inputDMA,
                                userLocationDma,
                                total: resultsLength,
                                viewed,
                                showLogo: show_logo,
                                viewedCallback: () => handleViewedCallback(job.hit.id)
                            }}
                        />
                        {enableJobAlert
                            ? (<DynamicJobAlertWrapper {...{ jobAlertsViewed, setJobAlertsViewed }} />)
                            : null}
                    </React.Fragment>
                );
            })}
        </>
    );
});
Tiles.displayName = "Tiles";

/**
 * Render job search results and handling user interactions with the search results page.
 */
const SearchResults = (props: ResultsProps) => {
    const { onClear } = props;
    const { publicRuntimeConfig: { museWebHost } } = getConfig();
    const { location: userLocation } = useUserState();
    const searchDispatch = useSearchDispatch();
    const searchResultsState = useSearchResultsState();
    const { totalCount, results } = searchResultsState;
    const searchResultsDispatch = useSearchResultsDispatch();
    const { ctsEnabled } = useSearchState();
    const [jobsHasBeenRequested, setJobsHasBeenRequested] = React.useState(false);
    const [userLocationDma, setUserLocationDma] = React.useState("");
    const [hasMore, setHasMore] = React.useState<boolean>(false);
    const [classState, setClassState] = React.useState(styles.resultsGrid);
    const [initialLoad, setInitialLoad] = React.useState(true);
    const searchState = useSearchState();
    const {
        company,
        location,
        keyword,
        filters,
        locationName,
        latitude,
        latlng,
        remoteOption,
        companyName,
        companyShortName,
        category,
        searchKeyword,
        userInteractions,
        url,
        preference,
        searchId
    } = searchResultsSelector(searchState);

    // eslint-disable-next-line arrow-body-style
    const jobLevel = React.useMemo(() => {
        return filters?.experienceLevel?.filter(Boolean)?.shift()?.split(",")?.shift() ?? "";
    }, [filters?.experienceLevel]);

    const [searchRequestState, setSearchRequestState] = React.useState({
        filters,
        companyShortName,
        searchKeyword,
        remoteOption,
        latlng,
        searchId
    });

    useDebounce(() => {
        // Handle any updated search params
        const updatedSearchRequestState = {
            filters,
            companyShortName,
            searchKeyword,
            remoteOption,
            latlng,
            searchId
        };

        if (!deepEqual(updatedSearchRequestState, searchRequestState)) {
            setSearchRequestState(updatedSearchRequestState);
        }
    }, 500, [
        company,
        location,
        keyword,
        filters
    ]);

    const router = useRouter() || { asPath: "" };
    const { asPath } = router;

    // This is the search id we want to use for tracking
    let uniqueSearchId = searchId;

    React.useEffect(() => {
        if (!(initialLoad && !!searchId)) {
            uniqueSearchId = uuidv4();
            searchDispatch({
                type: SearchIdActionTypes.updateSearchId,
                payload: uniqueSearchId
            });
        }
    }, [searchRequestState]);

    React.useEffect(() => {
        // We load the first set of search results server
        // side, so we don't want to fetch on initial load.
        if (!initialLoad || !results.length) {
            const urlPath = generateUrlPath({
                companyShortName,
                locationName: normalizeLocationName(locationName),
                searchKeyword,
                filters,
                asPath,
            });
            fetchJobSearchResults();
            searchDispatch({
                type: UrlActionTypes.updateUrl,
                payload: urlPath
            });
        }
        const searchEventProps: SnowplowSearchEventProps = {
            sp_search_type: "job",
            sp_search_id: uniqueSearchId,
            sp_search_keyword: searchKeyword
        };
        const isRemote = remoteOption === "virtual";
        const snowplowFilters = buildSnowplowFilterItems(filters, locationName, companyShortName);
        triggerSnowplowSearchEvent(searchEventProps, snowplowFilters, isRemote);
    }, [searchRequestState, userInteractions?.searchButtonClickedAt]);

    React.useEffect(() => {
        setHasMore(!!(totalCount - results.length));
    }, [totalCount, results.length]);

    /**
     * Fetch jobs with an optional cursor Id.
     *
     * @param cursor This is the cursor within the last job on the page.
     * @returns Promise
     */
    const fetchJobSearchResults = async (cursor?: string): Promise<void> => {
        setJobsHasBeenRequested(true);

        // if this is not a load more request and we are scrolled at all, snap back to the top.
        if (!cursor && window.scrollY) {
            window.scrollTo(0, 0);
        }

        const queryParams = getParams({
            filters,
            companyShortName,
            searchKeyword,
            remoteOption,
            latlng,
            cursor,
            searchApiPreference: preference
        });

        const jobsResponse = await fetchJobs(queryParams, ctsEnabled);
        const dispatchValues = formatJobsResponse(jobsResponse);
        const update = cursor
            ? [...searchResultsState.results, ...dispatchValues.results]
            : [...dispatchValues.results];
        searchResultsDispatch({
            type: "update_results",
            payload: {
                values: {
                    ...dispatchValues,
                    results: update
                }
            }
        });
        setJobsHasBeenRequested(false);
    };

    /**
     * Clear all the search filters
     */
    const clearSearch = () => {
        searchDispatch({
            type: FiltersActionTypes.resetAllFilters
        });
        searchDispatch({
            type: CompanyActionTypes.resetCompany
        });
        searchDispatch({
            type: LocationActionTypes.resetLocation
        });
        searchDispatch({
            type: KeywordActionTypes.resetKeyword
        });
        onClear?.();
    };

    /**
     * This is the event fired by the load more button.
     * @param event:  The click event.
     */
    const handleLoadMoreClick = React.useCallback((event?: React.MouseEvent<HTMLButtonElement>) => {
        if (event) {
            event.preventDefault();
        }
        fetchJobSearchResults(searchResultsState.cursor);
    }, [searchResultsState.cursor]);

    /**
     * setUserLocationDma are triggered when any of the following are modified:
     * userInputs location lat changes
     */
    React.useEffect(() => {
        setUserLocationDma(userLocation?.dma);
    }, [latitude]);

    /**
     * Push a new history state only after filter changes
     */
    React.useEffect(() => {
        if (typeof url !== "undefined") {
            window.history.pushState(null, null, `${url}${window.location.search}`);
        }
    }, [url]);

    React.useEffect(() => {
        const classes = cn(hasMore ? styles.hasFade : false, styles.resultsGrid);
        const shimmer = jobsHasBeenRequested ? styles.shimmer : null;
        setClassState(cn(shimmer, classes));
    }, [hasMore, jobsHasBeenRequested]);

    const searchResults = searchResultsState.results ?? [];
    const resultsLength = searchResults.length ?? 0;

    const getLDJson = (list: JobSearchHit[]) => {
        const itemListElement = list.map((job: JobSearchHit, index: number) => {
            const { apply_type, short_title } = job?.hit ?? {};
            const extension = apply_type === "external" ? "" : "#apply-inline-form";
            return {
                "@type": "ListItem",
                position: index + 1,

                url: `${museWebHost}/jobs/${job?.hit?.company?.short_name}/${short_title}${extension}`
            };
        });

        return JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement
        });
    };

    const tilesProps = {
        searchResults,
        locationName,
        userInteractions,
        userLocationDma,
        resultsLength,
        searchDispatch
    };

    React.useEffect(() => {
        if (initialLoad) {
            setInitialLoad(false);
        }
    }, []);

    return (
        <>
            <SearchHeader
                category={category}
                company={companyName ?? null}
                keyword={searchKeyword ?? null}
                level={jobLevel}
                location={locationName ?? null}
                state={searchResultsState}
            />
            <Head>
                <script
                    // using old style script and head so that this script will update on filter change.
                    type="application/ld+json"
                    id="ItemList"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: getLDJson(searchResults)
                    }}
                />
            </Head>
            {resultsLength
                ? (
                    <div className={classState}>
                        <Tiles {...tilesProps} />
                        {hasMore ? <LoadMore cb={handleLoadMoreClick} /> : null}
                    </div>
                )
                : <Empty requested={jobsHasBeenRequested} results={resultsLength} cb={clearSearch} />}
        </>
    );
};

SearchResults.displayName = "SearchResults";
export {
    SearchResults,
    getJobTileLocation,
    LoadMore
};
export type { GetJobTileLocationProps };
